import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { useParams } from 'react-router-dom';
import { getGroup, addMemberToGroup, removeMemberFromGroup, promoteToAdmin, demoteFromAdmin, leaveGroup, getGroupTasks, createTask, updateTask, updateTaskStatus, deleteTask, addTaskComment, reorderTasks, getTasksByUser, reorderUserTasks } from '../services/api';
import '../styles/Group.css';

function Group({ user }) {
  const { groupId } = useParams();
  
  const [group, setGroup] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('mine');
  
  // Modals
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  
  // Form states
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [invitationLink, setInvitationLink] = useState('');
  const [showInvitationLink, setShowInvitationLink] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newComment, setNewComment] = useState('');
  
  // Task form
  const [newTaskNumber, setNewTaskNumber] = useState('');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskLocation, setNewTaskLocation] = useState('');
  const [newTaskPrinterModel, setNewTaskPrinterModel] = useState('');
  const [newTaskType, setNewTaskType] = useState('');
  const [newTaskPhone, setNewTaskPhone] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
const [draggedTask, setDraggedTask] = useState(null);
const [searchQuery, setSearchQuery] = useState('');
const [statusFilter, setStatusFilter] = useState('all');
const [memberFilter, setMemberFilter] = useState('all');
const [taskTypeFilter, setTaskTypeFilter] = useState('all');
const [selectedTechnician, setSelectedTechnician] = useState(null);
const [technicianTasks, setTechnicianTasks] = useState([]);
const [loadingTechTasks, setLoadingTechTasks] = useState(false);
const socketRef = useRef(null);
  useEffect(() => {
    loadGroupData();

    // התחברות ל-Socket
    console.log('🔌 מנסה להתחבר ל-Socket...');
    socketRef.current = io('https://taskflow.zikprint.com', {
      withCredentials: true,
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('connect', () => {
      console.log('✅ Socket מחובר!', socketRef.current.id);
      // הצטרפות לחדר הקבוצה
      socketRef.current.emit('join-group', groupId);
    });

    socketRef.current.on('connect_error', (error) => {
      console.log('❌ שגיאת חיבור Socket:', error.message);
    });

    // האזנה לאירועים
    socketRef.current.on('task-created', (task) => {
      setTasks(prev => [task, ...prev]);
    });

    socketRef.current.on('task-updated', (updatedTask) => {
      setTasks(prev => prev.map(t => t._id === updatedTask._id ? updatedTask : t));
      if (selectedTask && selectedTask._id === updatedTask._id) {
        setSelectedTask(updatedTask);
      }
    });

    socketRef.current.on('task-deleted', (taskId) => {
      setTasks(prev => prev.filter(t => t._id !== taskId));
      if (selectedTask && selectedTask._id === taskId) {
        setShowTaskDetailsModal(false);
        setSelectedTask(null);
      }
    });

    // ניקוי בעת עזיבת הדף
    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave-group', groupId);
        socketRef.current.disconnect();
      }
    };
  }, [groupId]);

  const loadGroupData = async () => {
    try {
      const [groupRes, tasksRes] = await Promise.all([
        getGroup(groupId),
        getGroupTasks(groupId)
      ]);
      setGroup(groupRes.data.group);
      setTasks(tasksRes.data.tasks);
      setLoading(false);
    } catch (error) {
      console.error('שגיאה בטעינת נתונים:', error);
      setLoading(false);
    }
  };

  // Helper functions
const isAdmin = group && (
  group.adminId._id === user.id || 
  (group.admins && group.admins.some(a => a._id === user.id))
);
  
  const isAssignedToTask = (task) => {
    if (!task || !task.assignedTo) return false;
    return task.assignedTo.some(u => u._id === user.id);
  };

  const openTasks = tasks.filter(t => t.status !== 'closed');
  const closedTasks = tasks.filter(t => t.status === 'closed');
  const myTasks = tasks
    .filter(t => t.status !== 'closed' && isAssignedToTask(t))
    .sort((a, b) => {
      const orderA = a.userOrders?.find(uo => uo.userId === user?.id)?.order ?? 999;
      const orderB = b.userOrders?.find(uo => uo.userId === user?.id)?.order ?? 999;
      return orderA - orderB;
    });

const getDisplayName = (user) => {
    if (!user) return '';
    return user.displayName || user.name;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'open': 'פתוח',
      'in-progress': 'בטיפול',
      'completed': 'בוצע',
      'closed': 'סגור'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': '#2196F3',
      'in-progress': '#FF9800',
      'completed': '#4CAF50',
      'closed': '#757575'
    };
    return colors[status] || '#999';
  };

const getCurrentTasks = () => {
    let filtered = [];
    
    // שלב 1: סינון לפי לשונית
    switch (activeTab) {
      case 'mine': filtered = myTasks; break;
      case 'open': filtered = openTasks; break;
      case 'closed': filtered = closedTasks; break;
      default: filtered = openTasks;
    }
    
    // שלב 2: סינון לפי סטטוס (אם לא בלשונית סגורות)
    if (statusFilter !== 'all' && activeTab !== 'closed') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }
    
    // שלב 3: סינון לפי חבר משויך
    if (memberFilter !== 'all') {
      filtered = filtered.filter(task => 
        task.assignedTo && task.assignedTo.some(u => u._id === memberFilter)
      );
    }
    
    // שלב 4: סינון לפי סוג משימה
    if (taskTypeFilter !== 'all') {
      filtered = filtered.filter(task => task.taskType === taskTypeFilter);
    }
    
    // שלב 5: חיפוש טקסט
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(task => 
        (task.taskNumber && task.taskNumber.toLowerCase().includes(query)) ||
        (task.title && task.title.toLowerCase().includes(query)) ||
        (task.location && task.location.toLowerCase().includes(query)) ||
        (task.description && task.description.toLowerCase().includes(query)) ||
        (task.printerModel && task.printerModel.toLowerCase().includes(query)) ||
        (task.comments && task.comments.some(c => c.text.toLowerCase().includes(query)))
      );
    }
    
    return filtered;
  };
  // Task handlers
  const handleCreateTask = async () => {
    try {
      await createTask({
        groupId,
        taskNumber: newTaskNumber,
        title: newTaskTitle,
        description: newTaskDescription,
        location: newTaskLocation,
        printerModel: newTaskPrinterModel,
        taskType: newTaskType,
        phone: newTaskPhone,
        assignedTo: selectedMemberIds
      });
      resetTaskForm();
      setShowCreateTaskModal(false);
      loadGroupData();
    } catch (error) {
      console.error('שגיאה ביצירת משימה:', error);
      alert('שגיאה ביצירת משימה');
    }
  };

  const handleUpdateTask = async () => {
    try {
      await updateTask(editingTask._id, {
        taskNumber: newTaskNumber,
        title: newTaskTitle,
        description: newTaskDescription,
        location: newTaskLocation,
        printerModel: newTaskPrinterModel,
        taskType: newTaskType,
        phone: newTaskPhone,
        assignedTo: selectedMemberIds
      });
      resetTaskForm();
      setEditingTask(null);
      setShowEditTaskModal(false);
      loadGroupData();
    } catch (error) {
      console.error('שגיאה בעדכון משימה:', error);
      alert('שגיאה בעדכון משימה');
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await updateTaskStatus(taskId, newStatus);
      loadGroupData();
    } catch (error) {
      console.error('שגיאה בעדכון סטטוס:', error);
      alert('שגיאה בעדכון סטטוס');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את המשימה?')) return;
    try {
      await deleteTask(taskId);
      loadGroupData();
    } catch (error) {
      console.error('שגיאה במחיקת משימה:', error);
      alert('שגיאה במחיקת משימה');
    }
  };

  const resetTaskForm = () => {
    setNewTaskNumber('');
    setNewTaskTitle('');
    setNewTaskDescription('');
    setNewTaskLocation('');
    setNewTaskPrinterModel('');
    setNewTaskType('');
    setNewTaskPhone('');
    setSelectedMemberIds([]);
  };

  const openEditTaskModal = (task) => {
    setEditingTask(task);
    setNewTaskNumber(task.taskNumber || '');
    setNewTaskTitle(task.title || '');
    setNewTaskDescription(task.description || '');
    setNewTaskLocation(task.location || '');
    setNewTaskPrinterModel(task.printerModel || '');
    setNewTaskType(task.taskType || '');
    setNewTaskPhone(task.phone || '');
    setSelectedMemberIds(task.assignedTo.map(u => u._id));
    setShowEditTaskModal(true);
  };

  const openTaskDetails = (task) => {
    setSelectedTask(task);
    setShowTaskDetailsModal(true);
  };

  const toggleMemberSelection = (memberId) => {
    setSelectedMemberIds(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  // Comment handler
  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert('יש להזין טקסט להערה');
      return;
    }
    try {
      const response = await addTaskComment(selectedTask._id, newComment);
      setNewComment('');
      setSelectedTask(response.data.task);
      loadGroupData();
    } catch (error) {
      console.error('שגיאה בהוספת הערה:', error);
      alert(error.response?.data?.message || 'שגיאה בהוספת הערה');
    }
  };

  // Member handlers
  const handleAddMember = async () => {
    if (!newMemberEmail.trim()) {
      alert('יש להזין כתובת אימייל');
      return;
    }
    try {
      const response = await addMemberToGroup(groupId, newMemberEmail);
      if (response.data.invitationLink) {
        setInvitationLink(response.data.invitationLink);
        setShowInvitationLink(true);
      } else {
        setShowAddMemberModal(false);
        setNewMemberEmail('');
        loadGroupData();
      }
    } catch (error) {
      console.error('שגיאה בהוספת חבר:', error);
      alert(error.response?.data?.message || 'שגיאה בהוספת חבר');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('האם אתה בטוח שברצונך להסיר חבר זה?')) return;
    try {
      await removeMemberFromGroup(groupId, memberId);
      loadGroupData();
    } catch (error) {
      console.error('שגיאה בהסרת חבר:', error);
      alert('שגיאה בהסרת חבר');
    }
  };

const handlePromoteToAdmin = async (memberId) => {
    if (!window.confirm('האם אתה בטוח שברצונך לקדם משתמש זה למנהל?')) return;
    try {
      await promoteToAdmin(groupId, memberId);
      loadGroupData();
      alert('המשתמש קודם למנהל בהצלחה!');
    } catch (error) {
      console.error('שגיאה בקידום משתמש:', error);
      alert(error.response?.data?.message || 'שגיאה בקידום משתמש');
    }
  };

const handleDemoteFromAdmin = async (memberId) => {
    if (!window.confirm('האם אתה בטוח שברצונך להוריד משתמש זה מתפקיד מנהל?')) return;
    try {
      await demoteFromAdmin(groupId, memberId);
      loadGroupData();
      alert('המשתמש הורד מתפקיד מנהל בהצלחה!');
    } catch (error) {
      console.error('שגיאה בהורדה ממנהל:', error);
      alert(error.response?.data?.message || 'שגיאה בהורדה ממנהל');
    }
  };

const handleLeaveGroup = async () => {
    const isMainAdmin = group.adminId._id === user.id;
    const onlyMember = group.members.length <= 1;
    
    let confirmMessage = 'האם אתה בטוח שברצונך לעזוב את הקבוצה?';
    if (isMainAdmin && onlyMember) {
      confirmMessage = 'אתה היחיד בקבוצה. אם תעזוב, הקבוצה תימחק. להמשיך?';
    }
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const response = await leaveGroup(groupId);
      alert(response.data.message);
      // חזרה לדף הבית
      window.location.href = '/';
    } catch (error) {
      console.error('שגיאה בעזיבת קבוצה:', error);
      alert(error.response?.data?.message || 'שגיאה בעזיבת קבוצה');
    }
  };

  const copyInvitationLink = () => {
    navigator.clipboard.writeText(invitationLink);
    alert('הקישור הועתק ללוח!');
  };

// טעינת משימות לפי טכנאי
const loadTechnicianTasks = async (userId) => {
  setLoadingTechTasks(true);
  try {
    const response = await getTasksByUser(groupId, userId);
    setTechnicianTasks(response.data.tasks);
  } catch (error) {
    console.error('Error loading technician tasks:', error);
  }
  setLoadingTechTasks(false);
};

// בחירת טכנאי
const handleSelectTechnician = (member) => {
  setSelectedTechnician(member);
  loadTechnicianTasks(member._id);
};

// שמירת סדר משימות לטכנאי
const saveTechnicianOrder = async (reorderedTasks) => {
  console.log('saveTechnicianOrder called:', { groupId, technicianId: selectedTechnician._id, tasksCount: reorderedTasks.length });
  try {
    const result = await reorderUserTasks(groupId, selectedTechnician._id, reorderedTasks.map((task, index) => ({
      taskId: task._id,
      order: index
    })));
    console.log('reorderUserTasks result:', result);
  } catch (error) {
    console.error('Error saving technician order:', error);
  }
};

// גרירת משימות טכנאי
const handleTechnicianDragStart = (e, task) => {
  setDraggedTask(task);
  e.dataTransfer.effectAllowed = 'move';
};

const handleTechnicianDrop = async (e, targetTask) => {
  e.preventDefault();
  
  if (!draggedTask || draggedTask._id === targetTask._id) {
    setDraggedTask(null);
    return;
  }

  const draggedIndex = technicianTasks.findIndex(t => t._id === draggedTask._id);
  const targetIndex = technicianTasks.findIndex(t => t._id === targetTask._id);

  if (draggedIndex === -1 || targetIndex === -1) {
    setDraggedTask(null);
    return;
  }

  const newTasks = [...technicianTasks];
  newTasks.splice(draggedIndex, 1);
  newTasks.splice(targetIndex, 0, draggedTask);

  setTechnicianTasks(newTasks);
  await saveTechnicianOrder(newTasks);
  setDraggedTask(null);
};

// Drag and Drop handlers
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetTask) => {
    e.preventDefault();
    
    if (!draggedTask || draggedTask._id === targetTask._id) {
      setDraggedTask(null);
      return;
    }

    const currentTasks = getCurrentTasks();
    const draggedIndex = currentTasks.findIndex(t => t._id === draggedTask._id);
    const targetIndex = currentTasks.findIndex(t => t._id === targetTask._id);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedTask(null);
      return;
    }

    // יצירת מערך חדש עם הסדר המעודכן
    const newTasks = [...currentTasks];
    newTasks.splice(draggedIndex, 1);
    newTasks.splice(targetIndex, 0, draggedTask);

    // עדכון הסדר בכל המשימות
    const tasksToUpdate = newTasks.map((task, index) => ({
      taskId: task._id,
      order: index
    }));

    try {
      await reorderTasks(tasksToUpdate);
      loadGroupData();
    } catch (error) {
      console.error('שגיאה בעדכון סדר:', error);
      alert('שגיאה בעדכון סדר המשימות');
    }

    setDraggedTask(null);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
  };
  if (loading) {
    return <div className="page-loading">טוען...</div>;
  }

  if (!group) {
    return <div className="page-error">קבוצה לא נמצאה</div>;
  }
  return (
    <div className="group-page">
      {/* Header */}
      <div className="page-header" style={{ borderTop: `4px solid ${group.color}` }}>
<div className="page-header-top">
  <h2>{group.name}</h2>
  <div className="header-buttons">
    {isAdmin && (
      <button className="btn-add-member-top" onClick={() => setShowAddMemberModal(true)}>
        👥 הוסף חבר
      </button>
    )}
    <button className="btn-leave-group" onClick={handleLeaveGroup}>
      🚪 עזוב קבוצה
    </button>
  </div>
</div>
        
        {/* Tabs */}
        <div className="tabs">
          <button className={`tab ${activeTab === 'mine' ? 'active' : ''}`} onClick={() => setActiveTab('mine')}>
            📌 משויכות אלי ({myTasks.length})
          </button>
          <button className={`tab ${activeTab === 'open' ? 'active' : ''}`} onClick={() => setActiveTab('open')}>
            📋 פתוחות ({openTasks.length})
          </button>
          <button className={`tab ${activeTab === 'closed' ? 'active' : ''}`} onClick={() => setActiveTab('closed')}>
            ✅ סגורות ({closedTasks.length})
          </button>
          {isAdmin && (
            <button className={`tab ${activeTab === 'byTechnician' ? 'active' : ''}`} onClick={() => setActiveTab('byTechnician')}>
              👥 לפי טכנאי
            </button>
          )}
        </div>
</div>

      {/* Search and Filters */}
      <div className="search-filter-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="חיפוש לפי מספר קריאה, מיקום, תיאור, הערות..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>
        
        <div className="filters">
          {activeTab !== 'closed' && (
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">כל הסטטוסים</option>
              <option value="open">פתוח</option>
              <option value="in-progress">בטיפול</option>
              <option value="completed">בוצע</option>
            </select>
          )}
          
          <select 
            value={memberFilter} 
            onChange={(e) => setMemberFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">כל החברים</option>
            {group.members.map(member => (
              <option key={member._id} value={member._id}>
                {getDisplayName(member)}
              </option>
            ))}
          </select>

          <select 
            value={taskTypeFilter} 
            onChange={(e) => setTaskTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">כל הסוגים</option>
            <option value="קריאה">קריאה</option>
            <option value="התקנה">התקנה</option>
            <option value="החלפה">החלפה</option>
            <option value="משימה">משימה</option>
            <option value="אספקה">אספקה</option>
          </select>
          
          {(searchQuery || statusFilter !== 'all' || memberFilter !== 'all' || taskTypeFilter !== 'all') && (
            <button 
              className="clear-filters"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setMemberFilter('all');
                setTaskTypeFilter('all');
              }}
            >
              נקה הכל
            </button>
          )}
        </div>
      </div>

      {/* Add Task */}
      {isAdmin && activeTab !== 'closed' && (
        <div className="add-task-bar">
          <div className="add-task-input" onClick={() => setShowCreateTaskModal(true)}>
            <span className="plus-icon">+</span>
            <span>הוסף משימה חדשה</span>
          </div>
        </div>
      )}

      {/* By Technician Tab Content */}
      {activeTab === 'byTechnician' && isAdmin && (
        <div className="technician-view">
          <div className="technician-selector">
            <h4>בחר טכנאי:</h4>
            <div className="technician-list">
              {group.members.map(member => (
                <div 
                  key={member._id} 
                  className={`technician-item ${selectedTechnician && selectedTechnician._id === member._id ? 'selected' : ''}`}
                  onClick={() => handleSelectTechnician(member)}
                >
                  <img src={member.picture} alt={getDisplayName(member)} />
                  <span>{getDisplayName(member)}</span>
                  <span className="task-count">
                    ({tasks.filter(t => t.status !== 'closed' && t.assignedTo && t.assignedTo.some(u => u._id === member._id)).length})
                  </span>
                </div>
              ))}
            </div>
          </div>

          {selectedTechnician && (
            <div className="technician-tasks">
              <h4>משימות של {getDisplayName(selectedTechnician)}:</h4>
              <p className="drag-hint">💡 גרור משימות כדי לשנות את הסדר</p>
              
              {loadingTechTasks ? (
                <div className="loading">טוען...</div>
              ) : technicianTasks.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <p>אין משימות לטכנאי זה</p>
                </div>
              ) : (
                <div className="tasks-container">
                  {technicianTasks.map((task, index) => (
                    <div 
                      key={task._id} 
                      className={`task-item ${draggedTask && draggedTask._id === task._id ? 'dragging' : ''}`}
                      draggable={true}
                      onDragStart={(e) => handleTechnicianDragStart(e, task)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleTechnicianDrop(e, task)}
                      onDragEnd={handleDragEnd}
                      onClick={() => openTaskDetails(task)}
                    >
                      <div className="task-order-number">{index + 1}</div>
                      
                      <div 
                        className={`task-checkbox ${task.status}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {task.status === 'completed' && '✓'}
                      </div>
                      
                      <div className="task-content">
                        <div className="task-title-row">
                          {task.taskNumber && <span className="task-number-badge">#{task.taskNumber}</span>}
                          {task.taskType && <span className="task-type-badge">{task.taskType}</span>}
                          <h4 className={`task-title ${task.status === 'closed' ? 'closed' : ''}`}>
                            {task.title || 'משימה ללא כותרת'}
                          </h4>
                          <span className="task-status-badge" style={{ background: getStatusColor(task.status) }}>
                            {getStatusLabel(task.status)}
                          </span>
                        </div>
                        
                        <div className="task-meta">
                          {task.location && <span className="task-meta-item">📍 {task.location}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tasks List */}
      {activeTab !== 'byTechnician' && (
      <div className="tasks-container">
        {getCurrentTasks().length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <p>
              {activeTab === 'mine' && 'אין משימות משויכות אליך'}
              {activeTab === 'open' && 'אין משימות פתוחות'}
              {activeTab === 'closed' && 'אין משימות סגורות'}
            </p>
          </div>
        ) : (
          getCurrentTasks().map(task => (
            <div 
              key={task._id} 
              className={`task-item ${draggedTask && draggedTask._id === task._id ? 'dragging' : ''}`}
              onClick={() => openTaskDetails(task)}
              draggable={activeTab !== 'closed'}
              onDragStart={(e) => handleDragStart(e, task)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, task)}
              onDragEnd={handleDragEnd}
            >
              <div 
                className={`task-checkbox ${task.status}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (task.status !== 'closed' && (isAdmin || isAssignedToTask(task))) {
                    const nextStatus = task.status === 'open' ? 'in-progress' : 
                                      task.status === 'in-progress' ? 'completed' : 'open';
                    handleUpdateTaskStatus(task._id, nextStatus);
                  }
                }}
              >
                {task.status === 'completed' && '✓'}
              </div>
              
              <div className="task-content">
                <div className="task-title-row">
                  {task.taskNumber && <span className="task-number-badge">#{task.taskNumber}</span>}
                  {task.taskType && <span className="task-type-badge">{task.taskType}</span>}
                  <h4 className={`task-title ${task.status === 'closed' ? 'closed' : ''}`}>
                    {task.title || 'משימה ללא כותרת'}
                  </h4>
                  <span className="task-status-badge" style={{ background: getStatusColor(task.status) }}>
                    {getStatusLabel(task.status)}
                  </span>
                </div>
                
                <div className="task-meta">
                  {task.location && <span className="task-meta-item">📍 {task.location}</span>}
                  {task.assignedTo && task.assignedTo.length > 0 && (
                    <span className="task-meta-item">
                      <img src={task.assignedTo[0].picture} alt="" className="mini-avatar" />
                      {getDisplayName(task.assignedTo[0])}
                      {task.assignedTo.length > 1 && ` +${task.assignedTo.length - 1}`}
                    </span>
                  )}
                  {task.comments && task.comments.length > 0 && (
                    <span className="task-meta-item">💬 {task.comments.length}</span>
                  )}
                  <span className="task-meta-item">📅 {new Date(task.createdAt).toLocaleDateString('he-IL')} {new Date(task.createdAt).toLocaleTimeString('he-IL', {hour: '2-digit', minute: '2-digit'})}</span>
                  {task.statusUpdatedAt && (
                    <span className="task-meta-item">🔄 {new Date(task.statusUpdatedAt).toLocaleDateString('he-IL')} {new Date(task.statusUpdatedAt).toLocaleTimeString('he-IL', {hour: '2-digit', minute: '2-digit'})}</span>
                  )}
                </div>
              </div>

{(isAdmin || isAssignedToTask(task)) && task.status !== 'closed' && (
                <div className="task-actions">
                  <button className="task-action-btn" onClick={(e) => { e.stopPropagation(); openEditTaskModal(task); }}>✏️</button>
                  <select
                    className="status-dropdown"
                    value={task.status}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleUpdateTaskStatus(task._id, e.target.value)}
                  >
                    <option value="open">פתוח</option>
                    <option value="in-progress">בטיפול</option>
                    <option value="completed">בוצע</option>
                    {isAdmin && <option value="closed">סגור</option>}
                  </select>
                  {isAdmin && (
                    <button className="task-action-btn danger" onClick={(e) => { e.stopPropagation(); handleDeleteTask(task._id); }}>🗑️</button>
                  )}
                </div>
              )}

              {/* Reopen button for closed tasks */}
              {task.status === 'closed' && (isAdmin || isAssignedToTask(task)) && (
                <div className="task-actions">
                  <button 
                    className="btn-reopen"
                    onClick={(e) => { e.stopPropagation(); handleUpdateTaskStatus(task._id, 'open'); }}
                  >
                    🔄 פתח מחדש
                  </button>
                  {isAdmin && (
                    <button className="task-action-btn danger" onClick={(e) => { e.stopPropagation(); handleDeleteTask(task._id); }}>🗑️</button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      )}

{/* Members Panel */}
<div className="members-panel">
  <h3>👥 חברי הקבוצה ({group.members.length})</h3>
  <div className="members-list">
<div className="member-item">
  <img src={group.adminId.picture} alt={getDisplayName(group.adminId)} />
  <span>{getDisplayName(group.adminId)}</span>
  <span className="admin-badge">מנהל ראשי</span>
</div>
    {group.members.filter(m => m._id !== group.adminId._id).map(member => {
      const isMemberAdmin = group.admins && group.admins.some(a => a._id === member._id);
      
      return (
        <div key={member._id} className="member-item">
<img src={member.picture} alt={getDisplayName(member)} />
          <span>{getDisplayName(member)}</span>
          {isMemberAdmin && (
            <span className="admin-badge">מנהל</span>
          )}
{isAdmin && member._id !== user.id && (
  <div className="member-item-actions">
    {!isMemberAdmin && (
      <button 
        className="btn-promote-member" 
        onClick={() => handlePromoteToAdmin(member._id)}
        title="קדם למנהל"
      >
        👑
      </button>
    )}
    {isMemberAdmin && (
      <button 
        className="btn-demote-member" 
        onClick={() => handleDemoteFromAdmin(member._id)}
        title="הורד ממנהל"
      >
        👤
      </button>
    )}
    <button 
      className="btn-remove-member" 
      onClick={() => handleRemoveMember(member._id)}
      title="הסר חבר"
    >
      ✕
    </button>
  </div>
)}
        </div>
      );
    })}
  </div>
</div>

      {/* Create Task Modal */}
      {showCreateTaskModal && (
        <div className="modal-overlay" onClick={() => setShowCreateTaskModal(false)}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>משימה חדשה</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>מספר משימה</label>
                <input type="text" placeholder="לדוגמה: 12345" value={newTaskNumber} onChange={(e) => setNewTaskNumber(e.target.value)} className="input-field" />
              </div>
              <div className="form-group">
                <label>סוג משימה</label>
                <select value={newTaskType} onChange={(e) => setNewTaskType(e.target.value)} className="input-field">
                  <option value="">בחר סוג...</option>
                  <option value="קריאה">קריאה</option>
                  <option value="התקנה">התקנה</option>
                  <option value="החלפה">החלפה</option>
                  <option value="משימה">משימה</option>
                  <option value="אספקה">אספקה</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>כותרת</label>
              <input type="text" placeholder="כותרת המשימה" value={newTaskTitle} onChange={(e) => setNewTaskTitle(e.target.value)} className="input-field" autoFocus />
            </div>
            <div className="form-group">
              <label>תיאור</label>
              <textarea placeholder="תיאור המשימה..." value={newTaskDescription} onChange={(e) => setNewTaskDescription(e.target.value)} className="textarea-field" rows="3" />
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>מיקום</label>
                <input type="text" placeholder="לדוגמה: תל אביב" value={newTaskLocation} onChange={(e) => setNewTaskLocation(e.target.value)} className="input-field" />
              </div>
              <div className="form-group">
                <label>דגם מדפסת</label>
                <input type="text" placeholder="לדוגמה: HP LaserJet" value={newTaskPrinterModel} onChange={(e) => setNewTaskPrinterModel(e.target.value)} className="input-field" />
              </div>
            </div>
            <div className="form-group">
              <label>טלפון</label>
              <input type="tel" placeholder="לדוגמה: 050-1234567" value={newTaskPhone} onChange={(e) => setNewTaskPhone(e.target.value)} className="input-field" />
            </div>
            <div className="form-group">
              <label>שייך לחברים</label>
              <div className="members-select-list">
                {group.members.map(member => (
<div key={member._id} className={`member-select-item ${selectedMemberIds.includes(member._id) ? 'selected' : ''}`} onClick={() => toggleMemberSelection(member._id)}>
  <input type="checkbox" checked={selectedMemberIds.includes(member._id)} onChange={() => {}} />
  <img src={member.picture} alt={getDisplayName(member)} />
  <span>{getDisplayName(member)}</span>
</div>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-primary" onClick={handleCreateTask}>צור משימה</button>
              <button className="btn-secondary" onClick={() => { setShowCreateTaskModal(false); resetTaskForm(); }}>ביטול</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditTaskModal && editingTask && (
        <div className="modal-overlay" onClick={() => { setShowEditTaskModal(false); setEditingTask(null); }}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>עריכת משימה</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>מספר משימה</label>
                <input type="text" value={newTaskNumber} onChange={(e) => setNewTaskNumber(e.target.value)} className="input-field" />
              </div>
              <div className="form-group">
                <label>סוג משימה</label>
                <select value={newTaskType} onChange={(e) => setNewTaskType(e.target.value)} className="input-field">
                  <option value="">בחר סוג...</option>
                  <option value="קריאה">קריאה</option>
                  <option value="התקנה">התקנה</option>
                  <option value="החלפה">החלפה</option>
                  <option value="משימה">משימה</option>
                  <option value="אספקה">אספקה</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>כותרת</label>
              <input type="text" value={newTaskTitle} onChange={(e) => setNewTaskTitle(e.target.value)} className="input-field" />
            </div>
            <div className="form-group">
              <label>תיאור</label>
              <textarea value={newTaskDescription} onChange={(e) => setNewTaskDescription(e.target.value)} className="textarea-field" rows="3" />
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>מיקום</label>
                <input type="text" value={newTaskLocation} onChange={(e) => setNewTaskLocation(e.target.value)} className="input-field" />
              </div>
              <div className="form-group">
                <label>דגם מדפסת</label>
                <input type="text" value={newTaskPrinterModel} onChange={(e) => setNewTaskPrinterModel(e.target.value)} className="input-field" />
              </div>
            </div>
            <div className="form-group">
              <label>טלפון</label>
              <input type="tel" value={newTaskPhone} onChange={(e) => setNewTaskPhone(e.target.value)} className="input-field" />
            </div>
            <div className="form-group">
              <label>שייך לחברים</label>
              <div className="members-select-list">
                {group.members.map(member => (
<div key={member._id} className={`member-select-item ${selectedMemberIds.includes(member._id) ? 'selected' : ''}`} onClick={() => toggleMemberSelection(member._id)}>
  <input type="checkbox" checked={selectedMemberIds.includes(member._id)} onChange={() => {}} />
  <img src={member.picture} alt={getDisplayName(member)} />
  <span>{getDisplayName(member)}</span>
</div>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-primary" onClick={handleUpdateTask}>שמור</button>
              <button className="btn-secondary" onClick={() => { setShowEditTaskModal(false); setEditingTask(null); resetTaskForm(); }}>ביטול</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="modal-overlay" onClick={() => { setShowAddMemberModal(false); setNewMemberEmail(''); setInvitationLink(''); setShowInvitationLink(false); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>הוסף חבר לקבוצה</h3>
            {!showInvitationLink ? (
              <>
                <div className="form-group">
                  <label>כתובת אימייל</label>
                  <input type="email" placeholder="example@gmail.com" value={newMemberEmail} onChange={(e) => setNewMemberEmail(e.target.value)} className="input-field" autoFocus />
                </div>
                <div className="modal-actions">
                  <button className="btn-primary" onClick={handleAddMember}>הוסף</button>
                  <button className="btn-secondary" onClick={() => { setShowAddMemberModal(false); setNewMemberEmail(''); }}>ביטול</button>
                </div>
              </>
            ) : (
              <>
                <p style={{ color: '#4CAF50', fontWeight: 'bold' }}>✓ קישור הזמנה נוצר!</p>
                <p style={{ fontSize: '14px', color: '#666' }}>המשתמש לא קיים. שלח לו את הקישור:</p>
                <div className="invitation-link-box">
                  <input type="text" value={invitationLink} readOnly className="invitation-link-input" />
                  <button className="btn-copy" onClick={copyInvitationLink}>📋 העתק</button>
                </div>
                <div className="modal-actions">
                  <button className="btn-primary" onClick={() => { setShowAddMemberModal(false); setNewMemberEmail(''); setInvitationLink(''); setShowInvitationLink(false); }}>סגור</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

{/* Task Details Modal */}
      {showTaskDetailsModal && selectedTask && (
        <div className="modal-overlay" onClick={() => { setShowTaskDetailsModal(false); setSelectedTask(null); setNewComment(''); }}>
          <div className="modal-content modal-task-details" onClick={(e) => e.stopPropagation()}>
            
            {/* Header */}
            <div className="task-modal-header">
              <div className="task-modal-title">
                {selectedTask.taskNumber && <span className="task-number-badge large">#{selectedTask.taskNumber}</span>}
                <h3>{selectedTask.title || 'משימה ללא כותרת'}</h3>
              </div>
              <button className="close-btn" onClick={() => { setShowTaskDetailsModal(false); setSelectedTask(null); setNewComment(''); }}>✕</button>
            </div>

            {/* Status */}
            <div className="task-modal-status">
              <span className="task-status-badge large" style={{ background: getStatusColor(selectedTask.status) }}>
                {getStatusLabel(selectedTask.status)}
              </span>
            </div>

            {/* Info Grid */}
            <div className="task-modal-info">
              {selectedTask.taskType && (
                <div className="info-item info-highlight">
                  <span className="info-label">📂 סוג משימה</span>
                  <span className="task-type-badge large">{selectedTask.taskType}</span>
                </div>
              )}

              {selectedTask.description && (
                <div className="info-item">
                  <span className="info-label">📝 תיאור</span>
                  <p className="info-value">{selectedTask.description}</p>
                </div>
              )}

              <div className="info-grid-two">
                {selectedTask.location && (
                  <div className="info-item">
                    <span className="info-label">📍 מיקום</span>
                    <span className="info-value">{selectedTask.location}</span>
                  </div>
                )}
                
                {selectedTask.printerModel && (
                  <div className="info-item">
                    <span className="info-label">🖨️ דגם</span>
                    <span className="info-value">{selectedTask.printerModel}</span>
                  </div>
                )}

                {selectedTask.phone && (
                  <div className="info-item">
                    <span className="info-label">📞 טלפון</span>
                    <span className="info-value">
                      <a href={`tel:${selectedTask.phone}`} className="phone-link">{selectedTask.phone}</a>
                    </span>
                  </div>
                )}
              </div>

              <div className="info-divider"></div>

              <div className="info-grid-two">
                <div className="info-item">
                  <span className="info-label">👤 משוייך ל</span>
                  <div className="info-value">
                    {selectedTask.assignedTo && selectedTask.assignedTo.length > 0 ? (
                      <div className="assigned-users-list">
                        {selectedTask.assignedTo.map(u => (
                          <div key={u._id} className="assigned-user-chip">
                            <img src={u.picture} alt={getDisplayName(u)} />
                            <span>{getDisplayName(u)}</span>
                          </div>
                        ))}
                      </div>
                    ) : <span className="no-assign">לא משוייך</span>}
                  </div>
                </div>

                <div className="info-item">
                  <span className="info-label">✍️ נוצר על ידי</span>
                  <div className="info-value">
                    {selectedTask.createdBy && (
                      <div className="assigned-user-chip">
                        <img src={selectedTask.createdBy.picture} alt={getDisplayName(selectedTask.createdBy)} />
                        <span>{getDisplayName(selectedTask.createdBy)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="info-item">
                <span className="info-label">📅 נוצר בתאריך</span>
                <span className="info-value">{new Date(selectedTask.createdAt).toLocaleString('he-IL')}</span>
              </div>
            </div>

            {/* Comments Section */}
            <div className="task-modal-comments">
              <h4>💬 הערות ({selectedTask.comments ? selectedTask.comments.length : 0})</h4>
              
              <div className="comments-list">
                {selectedTask.comments && selectedTask.comments.length > 0 ? (
                  selectedTask.comments.map((comment, idx) => (
  <div key={idx} className="comment-item">
  <img src={comment.userId.picture} alt={getDisplayName(comment.userId)} />
  <div className="comment-body">
    <div className="comment-meta">
      <strong>{getDisplayName(comment.userId)}</strong>
                          <span>{new Date(comment.createdAt).toLocaleString('he-IL')}</span>
                        </div>
                        <p>{comment.text}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-comments">
                    <span>💭</span>
                    <p>אין הערות עדיין</p>
                  </div>
                )}
              </div>

              {/* Add Comment - Only for admin or assigned users, and not closed tasks */}
              {selectedTask.status !== 'closed' && (isAdmin || isAssignedToTask(selectedTask)) && (
                <div className="add-comment-section">
                  <textarea
                    placeholder="כתוב הערה..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows="3"
                  />
                  <button className="btn-primary" onClick={handleAddComment}>
                    שלח הערה
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

export default Group;